import React, { useEffect, useMemo, useState } from 'react'
import { BoltIcon, BuildingOffice2Icon, CheckIcon, ChevronDownIcon, ExclamationCircleIcon, MapPinIcon, PhoneIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import * as api from '../../api/rynek'
import BrandPicker from '../wykonawcy/BrandPicker'
import MapaPolski from '../wykonawcy/MapaPolski'
import ListaRozwijana from '../wykonawcy/ListaRozwijana'
import { REGIONY } from '../wykonawcy/regionyPolski'
import { inverterManufacturers, magazineManufacturers, panelManufacturers, wallboxManufacturers } from '../../filtersSchema'
import LogoMarki from '../oferta/LogoMarki'
import { SPECJALIZACJE_FIRMY, ZAKRESY_FIRMY, ZASOBY_FIRMY } from '../../config/rynek'

/**
 * Ankieta firmy na Rynku (2026-10-22, pełna ankieta 2026-10-23; `GET/PUT /api/v1/rynek/firma/profil`): pytania
 * z dawnego „Zostań partnerem” i zakres usług. Na tej podstawie dopasowujemy firmę do ogłoszeń; bez zapisanej ankiety
 * firma nie dostaje ogłoszeń na Rynku (backend odpowiada 403 `brak_ankiety`).
 * Niezapisana: karta z zachętą i otwarty formularz. Zapisana: jedna linia podsumowania z „Zmień”.
 * Zmienia konto główne albo administrator firmy; pozostali widzą informację.
 * `onZapisano`: rodzic odświeża to, co bramka zasłaniała (lista albo ogłoszenie). `otwarta`: zapisana ankieta
 * startuje rozwinięta (strona `/firma/ankieta`). Formularz jest podzielony na pięć kafelków z etapami (2026-11-01).
 * Autozapis (2026-11-02): niezapisane zmiany leżą w pamięci przeglądarki, osobno dla każdej firmy, i wracają po powrocie
 * na stronę (np. po przejściu do Google albo z wizytówki). Świadomie nie na serwerze: zapis ankiety wysyła zaproszenia.
 */

const MARKI_FALOWNIKI = Object.keys(inverterManufacturers)
const MARKI_MAGAZYNY = Object.keys(magazineManufacturers)
const MARKI_MODULY = Object.keys(panelManufacturers)
const MARKI_LADOWARKI = Object.keys(wallboxManufacturers)
const MAKS_OPISU = 2000
const NAZWY = new Map(REGIONY.map((r) => [r.key, r.nazwa]))

// Odpowiedzi jak w dawnym formularzu wykonawcy (backend `TERMINY_DNI`, `REAKCJE_H`).
const TERMINY = [
  { value: 7, label: 'Poniżej tygodnia' },
  { value: 14, label: 'Do 2 tygodni' },
  { value: 30, label: 'W ciągu miesiąca' },
  { value: 60, label: 'Powyżej miesiąca' },
]
const REAKCJE = [
  { value: 24, label: 'W ciągu 24 godzin' },
  { value: 48, label: 'W ciągu 48 godzin' },
  { value: 168, label: 'W ciągu tygodnia' },
]

const poleKlasa = 'h-[44px] w-full rounded-xl border-[1.5px] border-gray-300 bg-white px-3.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15'

const Pole = ({ etykieta, podpowiedz, wymagane, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-[13.5px] font-semibold text-gray-800">
      {etykieta}{wymagane && <span className="text-red-700"> *</span>}
      {podpowiedz && <span className="font-normal text-gray-500"> {podpowiedz}</span>}
    </span>
    {children}
  </div>
)

const LICZBA_ETAPOW = 5

const Etap = ({ numer, ikona: Ikona, tytul, children }) => (
  <section aria-label={`Etap ${numer}: ${tytul}`} className="flex flex-col gap-5 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-blue-600 text-white">
        <Ikona className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-col">
        <span className="text-xs text-gray-500">Etap {numer} z {LICZBA_ETAPOW}</span>
        <h3 className="m-0 text-base font-bold text-gray-900">{tytul}</h3>
      </div>
    </div>
    {children}
  </section>
)

const Przelacznik = ({ on, onClick, children }) => (
  <button type="button" aria-pressed={on} onClick={onClick}
          className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-full border-[1.5px] px-3.5 text-[13.5px] transition
                      ${on ? 'border-blue-600 bg-blue-600 font-semibold text-white' : 'border-gray-300 bg-white font-medium text-gray-700 hover:border-gray-400'}`}>
    {on && <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.6} aria-hidden="true" />}
    {children}
  </button>
)

const liczbaLubNull = (v) => (v === '' || v == null || !Number.isFinite(Number(v)) ? null : Number(v))

/** Wybrane marki z logotypami tam, gdzie je mamy; ładowarki logotypów jeszcze nie mają, więc pokazują nazwę. */
const WybraneMarki = ({ marki, rola }) => (marki.length > 0 ? (
  <ul className="m-0 mt-1 flex list-none flex-wrap items-center gap-x-3 gap-y-1.5 p-0">
    {marki.map((m) => (
      <li key={m}><LogoMarki producent={m} rola={rola} className="h-6 max-w-[90px]" zapas={<span className="text-[13px] text-gray-700">{m}</span>} /></li>
    ))}
  </ul>
) : null)

export const podsumowanie = (p) => [
  p.wojewodztwa.length === REGIONY.length ? 'cała Polska' : p.wojewodztwa.map((k) => NAZWY.get(k) || k).join(', '),
  ZAKRESY_FIRMY.filter((z) => p.zakres?.includes(z.klucz)).map((z) => z.ankieta.toLowerCase()).join(', '),
  [...p.marki_falownikow, ...p.marki_magazynow, ...(p.marki_modulow || []), ...(p.marki_ladowarek || [])].join(', '),
].filter(Boolean).join(' · ')

const kluczSzkicu = (profil) => (profil.firma?.id ? `vv_ankieta_szkic:${profil.firma.id}` : null)
const czytajSzkic = (klucz) => {
  if (!klucz) return null
  try { return JSON.parse(window.localStorage.getItem(klucz)) } catch { return null }
}
// Zablokowana albo pełna pamięć przeglądarki nie może psuć formularza: zostaje wtedy bez autozapisu.
const zapiszSzkic = (klucz, f) => {
  if (!klucz) return
  try { window.localStorage.setItem(klucz, JSON.stringify(f)) } catch { /* bez autozapisu */ }
}
const usunSzkic = (klucz) => {
  if (!klucz) return
  try { window.localStorage.removeItem(klucz) } catch { /* jak wyżej */ }
}

const poleZProfilu = (profil) => ({
  wojewodztwa: profil.wojewodztwa || [], zakres: profil.zakres || [],
  marki_falownikow: profil.marki_falownikow || [], marki_magazynow: profil.marki_magazynow || [],
  marki_modulow: profil.marki_modulow || [], marki_ladowarek: profil.marki_ladowarek || [],
  // Stare wolne teksty zostają w tablicy: backend przenosi je przy zapisie do „O nas”.
  specjalizacje: profil.specjalizacje || [], zasoby: profil.zasoby || [],
  telefony: [profil.telefony?.[0] || '', profil.telefony?.[1] || ''],
  od_roku: profil.od_roku ?? '', termin_dni: profil.termin_dni ?? '', gwarancja_lat: profil.gwarancja_lat ?? '',
  reakcja_awaria_h: profil.reakcja_awaria_h ?? '', opis: profil.opis || '', www: profil.www || '',
})

const INFO_DOMYSLNY = 'Ustawiliśmy Waszej firmie domyślnie całą Polskę oraz instalacje i montaż, żeby zaproszenia do zleceń trafiały do Was od razu. Zmieńcie to, jeśli działacie węziej, i zapiszcie ankietę.'

const Formularz = ({ profil, onZapisano, onAnuluj }) => {
  const klucz = kluczSzkicu(profil)
  const zapisane = useMemo(() => poleZProfilu(profil), [profil])
  // Tylko znane pola: szkic sprzed zmiany formularza nie może wnieść usuniętych pytań.
  const [f, setF] = useState(() => {
    const szkic = czytajSzkic(klucz) || {}
    return Object.fromEntries(Object.entries(zapisane).map(([k, v]) => [k, szkic[k] ?? v]))
  })
  const zmieniony = JSON.stringify(f) !== JSON.stringify(zapisane)
  const [przywrocony, setPrzywrocony] = useState(zmieniony)
  useEffect(() => {
    if (zmieniony) zapiszSzkic(klucz, f); else usunSzkic(klucz)
  }, [f, klucz, zmieniony])
  const odrzucSzkic = () => { setF(zapisane); setPrzywrocony(false) }
  const [busy, setBusy] = useState(false)
  const [blad, setBlad] = useState('')
  const ustaw = (klucz) => (v) => setF((x) => ({ ...x, [klucz]: v }))
  const przelaczListe = (klucz, wartosc) => setF((x) => ({
    ...x, [klucz]: x[klucz].includes(wartosc) ? x[klucz].filter((w) => w !== wartosc) : [...x[klucz], wartosc],
  }))
  const cala = f.wojewodztwa.length === REGIONY.length

  const zapisz = async (e) => {
    e.preventDefault()
    setBlad('')
    if (!f.wojewodztwa.length) { setBlad('Zaznacz województwa, w których montujecie.'); return }
    if (!f.zakres.length) { setBlad('Zaznacz, co robicie.'); return }
    setBusy(true)
    const r = await api.zapiszProfilFirmy({
      ...f,
      od_roku: liczbaLubNull(f.od_roku),
      termin_dni: liczbaLubNull(f.termin_dni), gwarancja_lat: liczbaLubNull(f.gwarancja_lat),
      reakcja_awaria_h: liczbaLubNull(f.reakcja_awaria_h),
      telefony: f.telefony.map((t) => t.trim()).filter(Boolean),
    })
    setBusy(false)
    if (!r.ok) { setBlad(r.error); return }
    usunSzkic(klucz)
    onZapisano(r.data)
  }

  return (
    <form onSubmit={zapisz} className="mt-4 flex flex-col gap-4" data-samouczek="ankieta">
      {przywrocony && (
        <p role="status" className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm leading-6 text-gray-700">
          Przywróciliśmy niezapisane zmiany z tej przeglądarki.
          <button type="button" onClick={odrzucSzkic} className="font-semibold text-blue-700 hover:underline">Odrzuć zmiany</button>
        </p>
      )}
      {profil.domyslny && (
        <p className="m-0 flex items-start gap-2 text-sm leading-6 text-gray-700">
          <MapPinIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden="true" />{INFO_DOMYSLNY}
        </p>
      )}
      <Etap numer={1} ikona={MapPinIcon} tytul="Gdzie i co robicie">
        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
          <Pole etykieta="Gdzie montujecie?" wymagane>
            <button type="button" onClick={() => setF((x) => ({ ...x, wojewodztwa: cala ? [] : REGIONY.map((r) => r.key) }))}
                    className="self-start text-sm font-semibold text-blue-700 hover:underline">
              {cala ? 'Odznacz wszystkie' : 'Zaznacz całą Polskę'}
            </button>
            <div className="max-w-[460px]">
              <MapaPolski wybrany={null} wybrane={f.wojewodztwa} onWybierz={(k) => k && przelaczListe('wojewodztwa', k)} />
            </div>
            {f.wojewodztwa.length > 0 && !cala && (
              <p className="m-0 text-[13px] text-gray-600">{f.wojewodztwa.map((k) => NAZWY.get(k) || k).join(', ')}</p>
            )}
          </Pole>
          <Pole etykieta="Co robicie?" podpowiedz="(można kilka)" wymagane>
            <div className="flex flex-wrap gap-2">
              {ZAKRESY_FIRMY.map((z) => (
                <Przelacznik key={z.klucz} on={f.zakres.includes(z.klucz)} onClick={() => przelaczListe('zakres', z.klucz)}>{z.ankieta}</Przelacznik>
              ))}
            </div>
          </Pole>
        </div>
      </Etap>
      <Etap numer={2} ikona={BuildingOffice2Icon} tytul="Firma i warunki">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Pole etykieta="Od którego roku działacie?" podpowiedz="(opcjonalnie)">
            <input inputMode="numeric" value={f.od_roku} onChange={(e) => ustaw('od_roku')(e.target.value.replace(/\D/g, '').slice(0, 4))}
                   placeholder="np. 2016" aria-label="Rok rozpoczęcia działalności" className={poleKlasa} />
          </Pole>
          <Pole etykieta="Średni termin montażu od umowy" podpowiedz="(opcjonalnie)">
            <ListaRozwijana opcje={TERMINY} value={f.termin_dni} onChange={ustaw('termin_dni')} ariaLabel="Średni termin montażu" />
          </Pole>
          <Pole etykieta="Gwarancja na montaż" podpowiedz="(lata, opcjonalnie)">
            <input inputMode="numeric" value={f.gwarancja_lat} onChange={(e) => ustaw('gwarancja_lat')(e.target.value.replace(/\D/g, '').slice(0, 2))}
                   placeholder="np. 5" aria-label="Gwarancja na montaż w latach" className={poleKlasa} />
          </Pole>
          <Pole etykieta="Czas reakcji na awarię" podpowiedz="(opcjonalnie)">
            <ListaRozwijana opcje={REAKCJE} value={f.reakcja_awaria_h} onChange={ustaw('reakcja_awaria_h')} ariaLabel="Czas reakcji na awarię" />
          </Pole>
        </div>
      </Etap>
      <Etap numer={3} ikona={BoltIcon} tytul="Sprzęt, na którym pracujecie">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Pole etykieta="Falowniki, na których pracujecie najczęściej" podpowiedz="(do 10)">
            <BrandPicker options={MARKI_FALOWNIKI} value={f.marki_falownikow} onChange={ustaw('marki_falownikow')} max={10}
                         ariaLabel="Marki falowników" />
            <WybraneMarki marki={f.marki_falownikow} rola="falownik" />
          </Pole>
          <Pole etykieta="Magazyny energii" podpowiedz="(do 10)">
            <BrandPicker options={MARKI_MAGAZYNY} value={f.marki_magazynow} onChange={ustaw('marki_magazynow')} max={10}
                         ariaLabel="Marki magazynów energii" />
            <WybraneMarki marki={f.marki_magazynow} rola="magazyn" />
          </Pole>
          <Pole etykieta="Moduły PV" podpowiedz="(opcjonalnie, do 10; nie wpływają na dopasowanie do zleceń)">
            <BrandPicker options={MARKI_MODULY} value={f.marki_modulow} onChange={ustaw('marki_modulow')} max={10}
                         ariaLabel="Marki modułów PV" />
            <WybraneMarki marki={f.marki_modulow} rola="moduly" />
          </Pole>
          <Pole etykieta="Ładowarki EV" podpowiedz="(do 10)">
            <BrandPicker options={MARKI_LADOWARKI} value={f.marki_ladowarek} onChange={ustaw('marki_ladowarek')} max={10}
                         ariaLabel="Marki ładowarek EV" />
            <WybraneMarki marki={f.marki_ladowarek} rola="ladowarka" />
          </Pole>
        </div>
      </Etap>
      <Etap numer={4} ikona={WrenchScrewdriverIcon} tytul="Specjalizacje i zasoby">
        {SPECJALIZACJE_FIRMY.map((g) => (
          <Pole key={g.grupa} etykieta={g.grupa} podpowiedz="(opcjonalnie, można kilka)">
            <div className="flex flex-wrap gap-2">
              {g.pozycje.map((n) => (
                <Przelacznik key={n} on={f.specjalizacje.includes(n)} onClick={() => przelaczListe('specjalizacje', n)}>{n}</Przelacznik>
              ))}
            </div>
          </Pole>
        ))}
        <Pole etykieta="Zasoby" podpowiedz="(opcjonalnie, można kilka)">
          <div className="flex flex-wrap gap-2">
            {ZASOBY_FIRMY.map((n) => (
              <Przelacznik key={n} on={f.zasoby.includes(n)} onClick={() => przelaczListe('zasoby', n)}>{n}</Przelacznik>
            ))}
          </div>
        </Pole>
      </Etap>
      <Etap numer={5} ikona={PhoneIcon} tytul="Kontakt i opis firmy">
        <Pole etykieta="Telefony do firmy" podpowiedz="(opcjonalnie, do 2; klient zobaczy je po wyborze Waszej oferty)">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {f.telefony.map((t, i) => (
              <input key={i} type="tel" inputMode="tel" maxLength={20} value={t} aria-label={`Telefon ${i + 1}`} placeholder="np. 500 000 000"
                     onChange={(e) => setF((x) => ({ ...x, telefony: x.telefony.map((w, k) => (k === i ? e.target.value : w)) }))} className={poleKlasa} />
            ))}
          </div>
        </Pole>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Pole etykieta="O nas" podpowiedz={`(opcjonalnie, ${f.opis.length} z ${MAKS_OPISU} znaków)`}>
            <textarea rows={5} maxLength={MAKS_OPISU} value={f.opis} onChange={(e) => ustaw('opis')(e.target.value)}
                      aria-label="O nas" placeholder="np. Montujemy od 2016 roku, głównie magazyny energii i instalacje na gruncie."
                      className="w-full rounded-xl border-[1.5px] border-gray-300 bg-white px-3.5 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15" />
          </Pole>
          <Pole etykieta="Strona internetowa" podpowiedz="(opcjonalnie)">
            <input value={f.www} onChange={(e) => ustaw('www')(e.target.value)} maxLength={200} placeholder="np. firma.pl"
                   aria-label="Strona internetowa" className={poleKlasa} />
          </Pole>
        </div>
      </Etap>
      {blad && (
        <p role="alert" className="m-0 flex items-start gap-2 text-sm text-red-700">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{blad}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy}
                className="h-[46px] rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
          {busy ? 'Zapisuję…' : 'Zapisz ankietę'}
        </button>
        {onAnuluj && <button type="button" onClick={() => { usunSzkic(klucz); onAnuluj() }} className="px-2 text-sm font-semibold text-gray-600">Anuluj</button>}
      </div>
      {zmieniony && (
        <p className="m-0 text-[13px] text-gray-600">Zmiany zachowujemy na bieżąco w tej przeglądarce. Zleceń dotyczą dopiero po kliknięciu „Zapisz ankietę”.</p>
      )}
    </form>
  )
}

// `naStronie` (strona `/firma/ankieta`, 2026-10-24): opis stoi w nagłówku strony, więc karta go nie powtarza, a błąd
// odczytu widać zamiast pustego miejsca.
// `wKafelku` (uwagi przeglądu F4): część kafelka „Wizytówka” na Rynku ofert, bez własnej ramki.
const ProfilNaRynku = ({ onZapisano, otwarta = false, naStronie = false, wKafelku = false }) => {
  const [profil, setProfil] = useState(null)
  const [blad, setBlad] = useState('')
  const [edycja, setEdycja] = useState(otwarta)

  useEffect(() => {
    api.profilFirmy().then((r) => { if (r.ok) setProfil(r.data); else setBlad(r.error || 'Nie udało się wczytać ankiety.') })
  }, [])

  const opis = useMemo(() => (profil ? podsumowanie(profil) : ''), [profil])
  if (!profil) {
    return naStronie && blad
      ? <p role="alert" className="m-0 rounded-[18px] border border-gray-200 bg-white p-5 text-sm text-red-700">{blad}</p>
      : null
  }
  const zapisano = (p) => { setProfil(p); setEdycja(false); onZapisano?.(p) }

  if (!profil.uzupelniony) {
    return (
      <section aria-labelledby={naStronie ? undefined : 'ankieta-firmy'} aria-label={naStronie ? 'Ankieta firmy' : undefined}
               className={naStronie ? '' : 'rounded-[20px] border-2 border-blue-600 bg-white p-5 sm:p-6'}>
        {!naStronie && (
          <>
            <h2 id="ankieta-firmy" className="m-0 text-lg font-bold">Uzupełnij ankietę firmy</h2>
            <p className="m-0 mt-1 max-w-2xl text-sm leading-6 text-gray-600">
              Dwie minuty: gdzie montujecie, co robicie i na jakim sprzęcie pracujecie. Na tej podstawie dopasowujemy Waszą
              firmę do zleceń klientów z okolicy.
            </p>
          </>
        )}
        {profil.moze_edytowac
          ? <Formularz profil={profil} onZapisano={zapisano} />
          : <p className="m-0 mt-3 text-sm text-gray-700">Ankietę wypełnia konto główne albo administrator firmy.</p>}
      </section>
    )
  }
  return (
    <>
      <section aria-label="Ankieta firmy na Rynku" className={wKafelku ? "" : "rounded-[18px] border border-gray-200 bg-white p-4 sm:p-5"}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="m-0 flex min-w-0 items-center gap-2 text-sm text-gray-700">
            <MapPinIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
            <span className="font-semibold text-gray-900">{profil.domyslny ? 'Profil domyślny:' : 'Profil na Rynku:'}</span>
            <span className="truncate">{opis}</span>
          </p>
          {!(profil.specjalizacje || []).length && profil.moze_edytowac && (
            <p className="m-0 w-full text-[13px] text-gray-600">Zaznaczcie specjalizacje, żeby dostawać lepiej dopasowane zlecenia.</p>
          )}
          <span className="flex flex-wrap items-center gap-4">
            {profil.moze_edytowac && (
              <button type="button" onClick={() => setEdycja((x) => !x)} aria-expanded={edycja}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline">
                {edycja ? 'Zwiń' : 'Zmień'}
                <ChevronDownIcon className={`h-4 w-4 transition ${edycja ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
            )}
          </span>
        </div>
      </section>
      {edycja && <Formularz profil={profil} onZapisano={zapisano} onAnuluj={() => setEdycja(false)} />}
    </>
  )
}

export default ProfilNaRynku
